import { Link, useNavigate, useParams } from "react-router-dom";
import { useSyncExternalStore } from "react";
import { MessageCircle, Globe, LogOut, ClipboardList, ChevronRight, Smartphone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useMe } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { canOfferInstallFromMenu, requestOpenInstallPrompt, subscribeInstallState } from "@/lib/pwa";

/**
 * Perfil do aluno + dados do trainer (white-label) + logout. Esta tela já é
 * funcional (não stub) — serve de prova do fluxo auth + API ponta a ponta.
 */
export function PerfilView() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const me = useMe();
  const canInstall = useSyncExternalStore(
    subscribeInstallState,
    () => canOfferInstallFromMenu(slug),
    () => false,
  );

  async function handleLogout() {
    await signOut();
    navigate(`/s/${slug}/login`, { replace: true });
  }

  if (me.isLoading) return <Spinner fullscreen />;

  const student = me.data;
  const trainer = student?.trainer;
  const whatsappHref = trainer?.whatsapp_number
    ? `https://wa.me/${trainer.whatsapp_number.replace(/\D/g, "")}`
    : null;

  return (
    <div className="animate-fade-in px-5 pt-8">
      <header className="mb-6 flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-surface text-xl font-display text-accent-soft">
          {(student?.name ?? "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl text-ink">{student?.name}</h1>
          {student?.email ? <p className="truncate text-sm text-muted">{student.email}</p> : null}
        </div>
      </header>

      {trainer ? (
        <section className="mb-6 rounded-2xl border border-border-subtle bg-surface p-5">
          <p className="mb-3 text-xs font-medium uppercase tracking-wide text-faint">
            Seu treinador
          </p>
          <div className="flex items-center gap-3">
            {trainer.logo_url || trainer.avatar_url ? (
              <img
                src={trainer.logo_url ?? trainer.avatar_url ?? ""}
                alt={trainer.full_name ?? "Treinador"}
                className="h-12 w-12 rounded-full object-cover ring-1 ring-border-strong"
              />
            ) : null}
            <div className="min-w-0">
              <p className="truncate font-medium text-ink">{trainer.full_name ?? "Treinador"}</p>
              {trainer.bio ? <p className="line-clamp-2 text-sm text-muted">{trainer.bio}</p> : null}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {whatsappHref ? (
              <a
                href={whatsappHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-3 py-2 text-sm text-ink transition-colors hover:bg-accent-hover"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
            ) : null}
            {trainer.website ? (
              <a
                href={trainer.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-border-subtle px-3 py-2 text-sm text-muted transition-colors hover:text-ink"
              >
                <Globe size={16} /> Site
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      <Link
        to={`/s/${slug}/anamnese`}
        className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-surface p-5 transition-all hover:border-border-strong hover:bg-surface-hover"
      >
        <div className="flex min-w-0 items-center gap-3">
          <ClipboardList size={20} className="shrink-0 text-accent-soft" aria-hidden />
          <div className="min-w-0">
            <p className="font-medium text-ink">Minha anamnese</p>
            <p className="truncate text-sm text-muted">
              Preencha sua ficha e envie pro seu personal
            </p>
          </div>
        </div>
        <ChevronRight size={18} className="shrink-0 text-faint" aria-hidden />
      </Link>

      {canInstall ? (
        <button
          type="button"
          onClick={requestOpenInstallPrompt}
          className="mb-6 flex w-full items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-surface p-5 text-left transition-all hover:border-border-strong hover:bg-surface-hover"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Smartphone size={20} className="shrink-0 text-accent-soft" aria-hidden />
            <div className="min-w-0">
              <p className="font-medium text-ink">Instalar o app</p>
              <p className="truncate text-sm text-muted">Seus treinos direto na tela inicial do celular</p>
            </div>
          </div>
          <ChevronRight size={18} className="shrink-0 text-faint" aria-hidden />
        </button>
      ) : null}

      <button
        onClick={handleLogout}
        className="inline-flex items-center gap-2 rounded-xl border border-border-subtle px-4 py-3 text-sm text-muted transition-colors hover:border-border-strong hover:text-ink"
      >
        <LogOut size={16} /> Sair
      </button>
    </div>
  );
}
